import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { memo, useMemo } from "react";
import { useWidgetConfig, ConciergeHomeLayoutWidget, ConciergeHomeStartersWidget, ConciergeHomeBrandLabelWidget, ConciergeHomeLogoWidget, ConciergeHomeSubtitleWidget, ConciergeHomeGreetingWidget } from "@brandclub/bc-widgets";
import { PromptInputConcierge } from "./PromptInputConcierge/index.js";
import { useConversationStarters } from "../../utils/hooks/useConversationStarters.js";
const ConciergeWelcomeFullPageComponent = ({
  displayQueries,
  isLoadingSuggestedQueries,
  onSearch,
  pageContext,
  apiBaseUrl,
  brandLogoUrl,
  brandName = "",
  userName,
  conciergeName = "Concierge",
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  isMobile: isMobileProp = false
}) => {
  const pageType = pageContext == null ? void 0 : pageContext.pageType;
  const stacklineSku = pageContext == null ? void 0 : pageContext.stacklineSku;
  const chatInputConfig = useWidgetConfig("concierge-chat-input", {});
  const inputPlaceholder = chatInputConfig.placeholderDefault || "Ask me anything";
  const { questions: contextQueries, isLoading: isLoadingContextQueries } = useConversationStarters({
    stacklineSku,
    apiBaseUrl,
    enabled: pageType === "pdp" && !!stacklineSku
  });
  const queriesToShow = useMemo(
    () => contextQueries.length > 0 ? contextQueries : displayQueries,
    [contextQueries, displayQueries]
  );
  const isLoading = useMemo(
    () => isLoadingContextQueries || (pageContext == null ? void 0 : pageContext.pageType) === "pdp" && isLoadingSuggestedQueries,
    [isLoadingContextQueries, pageContext == null ? void 0 : pageContext.pageType, isLoadingSuggestedQueries]
  );
  return /* @__PURE__ */ jsx(ConciergeHomeLayoutWidget, { widgetId: "concierge-home-layout", displayName: "Home Layout", children: (layoutState, resolveColor) => {
    const isMobile = isMobileProp || typeof window !== "undefined" && window.innerWidth < 768;
    const logoPos = isMobile && layoutState.mobile_logoPosition !== "inherit" ? layoutState.mobile_logoPosition : layoutState.logoPosition;
    const startersMode = isMobile && layoutState.mobile_startersMode !== "inherit" ? layoutState.mobile_startersMode : layoutState.startersMode;
    const bgColor = layoutState.backgroundStyle === "colored" ? resolveColor(layoutState.backgroundColor) : "#ffffff";
    const startersElement = startersMode !== "hidden" && queriesToShow.length > 0 && !isLoading ? /* @__PURE__ */ jsx(
      ConciergeHomeStartersWidget,
      {
        mode: startersMode,
        elementStyle: layoutState.elementStyle,
        cardLayoutDirection: layoutState.cardLayoutDirection,
        maxStarters: layoutState.maxStarters,
        queries: queriesToShow,
        onSearch
      }
    ) : null;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          minHeight: 0,
          boxSizing: "border-box",
          overflow: isMobile ? "hidden" : "auto",
          backgroundColor: bgColor
        },
        children: [
          /* @__PURE__ */ jsx("div", { style: { flex: 1 } }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: isMobile ? "16px 16px" : "24px 16px",
                flexShrink: 0
              },
              children: [
                logoPos === "hero" && /* @__PURE__ */ jsxs(Fragment, { children: [
                  layoutState.brandLabelPosition === "above-hero-logo" && /* @__PURE__ */ jsx(ConciergeHomeBrandLabelWidget, {}),
                  /* @__PURE__ */ jsx(ConciergeHomeLogoWidget, { logoUrl: brandLogoUrl }),
                  layoutState.subtitlePosition === "under-hero-logo" && /* @__PURE__ */ jsx(ConciergeHomeSubtitleWidget, { brandName, conciergeName })
                ] }),
                layoutState.showGreeting && /* @__PURE__ */ jsx(
                  ConciergeHomeGreetingWidget,
                  {
                    userName,
                    showIcon: layoutState.showGreetingIcon,
                    iconPosition: layoutState.greetingIconPosition
                  }
                ),
                layoutState.subtitlePosition === "under-greeting" && /* @__PURE__ */ jsx(ConciergeHomeSubtitleWidget, { brandName, conciergeName }),
                !isMobile && handleSearch && setSearchInputValue && /* @__PURE__ */ jsx("div", { style: { width: "100%", maxWidth: 768, marginTop: layoutState.searchInputGap ?? 24 }, children: /* @__PURE__ */ jsx(
                  PromptInputConcierge,
                  {
                    placeholder: inputPlaceholder,
                    value: searchInputValue ?? "",
                    onChange: setSearchInputValue,
                    onSubmit: handleSearch,
                    isStreaming: isActivelyStreaming
                  }
                ) }),
                !isMobile && startersElement,
                !isMobile && logoPos === "after-content" && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginTop: layoutState.footerGap,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    },
                    children: [
                      layoutState.brandLabelPosition === "above-footer-logo" && /* @__PURE__ */ jsx(ConciergeHomeBrandLabelWidget, {}),
                      /* @__PURE__ */ jsx(ConciergeHomeLogoWidget, { logoUrl: brandLogoUrl })
                    ]
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx("div", { style: { flex: isMobile ? 1 : 1.2 } }),
          isMobile && startersElement && /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                flexShrink: 0,
                padding: "0 8px 0 8px"
              },
              children: startersElement
            }
          ),
          isMobile && handleSearch && setSearchInputValue && /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                flexShrink: 0,
                paddingTop: 16,
                paddingLeft: 8,
                paddingRight: 8,
                paddingBottom: "max(24px, env(safe-area-inset-bottom))"
              },
              children: /* @__PURE__ */ jsx(
                PromptInputConcierge,
                {
                  placeholder: "Ask me anything",
                  value: searchInputValue ?? "",
                  onChange: setSearchInputValue,
                  onSubmit: handleSearch,
                  isStreaming: isActivelyStreaming,
                  compactMobile: true
                }
              )
            }
          ),
          !isMobile && logoPos === "footer" && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                flexShrink: 0,
                paddingBottom: layoutState.footerPaddingBottom ?? 16,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: layoutState.footerGap
              },
              children: [
                layoutState.brandLabelPosition === "above-footer-logo" && /* @__PURE__ */ jsx(ConciergeHomeBrandLabelWidget, {}),
                /* @__PURE__ */ jsx(ConciergeHomeLogoWidget, { logoUrl: brandLogoUrl })
              ]
            }
          )
        ]
      }
    );
  } });
};
const ConciergeWelcomeFullPage = memo(ConciergeWelcomeFullPageComponent);
export {
  ConciergeWelcomeFullPage
};
//# sourceMappingURL=ConciergeWelcomeFullPage.js.map
